import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  completeUpdateIdentity,
  getPatientDetailsById,
  getPatientIntakeFormAnswers,
  getProviderPatients,
  PatientDetails,
  updateIdentity,
  updatePatientById,
  UpdatePatientPayload,
} from "../../../fay/Api";
import { useIdentity } from "../../../provider-portal/IdentityProvider";
import { useState } from "react";

const usePatients = (options?: { withOldPatients?: boolean }) => {
  const { withOldPatients } = options ?? {};
  const { providerId } = useIdentity();

  const {
    data: patients,
    isLoading: isLoadingPatients,
    isFetched,
  } = useQuery({
    queryKey: ["patients", providerId, { withOldPatients }],

    queryFn: async () => {
      const patientsResponse = await getProviderPatients({ withOldPatients });

      return patientsResponse.patients;
    },

    initialData: [] as PatientDetails[],
  });

  return {
    patients,
    isLoadingPatients: isLoadingPatients || !providerId,
    hasFetchedPatients: isFetched,
  };
};

export const usePatientDetails = (
  patientId: string | null | undefined,
  options?: { staleTime?: number; includeNextAppointment?: boolean },
) => {
  const queryKey = ["patient", patientId, options?.includeNextAppointment];

  const {
    data: patient,
    isLoading,
    isFetching,
    isFetched,
    isError,
  } = useQuery({
    queryKey,
    queryFn: async () =>
      await getPatientDetailsById(
        patientId!,
        !!options?.includeNextAppointment,
      ),
    enabled: !!patientId,
    // A 404 or 403 is normal for this, so we don't need to retry unless
    // we refocus.
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: options?.staleTime || 0,
  });

  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const { mutateAsync: updatePatient, isPending: isUpdatingPatient } =
    useMutation({
      mutationFn: async (updatePatientPayload: UpdatePatientPayload) => {
        if (!patientId) {
          throw new Error("No patient ID provided");
        }
        return updatePatientById(patientId, updatePatientPayload);
      },
      onSuccess: (response) => {
        queryClient.setQueryData(["patient", patientId], response);
      },
    });

  const [
    updatePatientIdentityChallengeId,
    setUpdatePatientIdentityChallengeId,
  ] = useState<string | null>(null);

  const {
    mutateAsync: updatePatientIdentity,
    isPending: isUpdatingPatientIdentity,
  } = useMutation({
    mutationFn: async ({
      email,
      phoneNumber,
    }: {
      email?: string;
      phoneNumber?: string;
    }) => {
      return updateIdentity(email, phoneNumber);
    },
    onSuccess: (response) => {
      setUpdatePatientIdentityChallengeId(response.challenge_id);
    },
  });

  const {
    mutateAsync: completeUpdatePatientIdentity,
    isPending: isCompletingUpdatingPatientIdentity,
  } = useMutation({
    mutationFn: async (code: string) => {
      if (!updatePatientIdentityChallengeId) {
        throw new Error("No challenge ID");
      }

      return completeUpdateIdentity(updatePatientIdentityChallengeId, code);
    },
    onSuccess: async () => {
      await invalidate();
    },
  });

  return {
    patient,
    couldNotLoadPatientDetails: isError,
    isLoading,
    isFetching,
    isLoadingInitial: isLoading && !isFetched,
    updatePatient,
    isUpdatingPatient,
    invalidate,
    updatePatientIdentity,
    isUpdatingPatientIdentity,
    completeUpdatePatientIdentity,
    isCompletingUpdatingPatientIdentity,
  };
};

export const usePatientIntakeFormAnswers = (
  patientId: string | null | undefined,
  options?: { staleTime?: number },
) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["patient-intake-form-answers", patientId],
    queryFn: async () => await getPatientIntakeFormAnswers(patientId!),
    // 403s are normal for this, so we don't need to retry unless
    // we refocus.
    retry: false,
    enabled: !!patientId,
    staleTime: options?.staleTime || 0,
  });

  return {
    patientIntakeFormAnswers: data,
    isPatientIntakeFormAnswersLoading: isLoading,
    couldNotLoadPatientIntakeFormAnswers: isError,
  };
};

export default usePatients;
